import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Grid, Box, Typography, useTheme } from "@mui/material";
import { Inbox } from "@mui/icons-material";
import UnifiedAppCard from "./UnifiedAppCard";
import AppCardSkeleton from "./AppCardSkeleton";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1],
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: 0.2 },
  },
};

/**
 * Empty state component
 */
const EmptyState = ({ isDarkMode }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      py: 8,
      color: isDarkMode ? "#64748b" : "#94a3b8",
    }}
  >
    <Inbox sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
    <Typography variant="h6" sx={{ fontWeight: 500 }}>
      No applications found
    </Typography>
    <Typography variant="body2" sx={{ mt: 1, textAlign: "center" }}>
      Create your first application to get started
    </Typography>
  </Box>
);

/**
 * Card grid view component - displays apps in a responsive grid
 */
export function CardGridView({
  apps,
  loading,
  onOpenPipeline,
  onRestart,
  onRebuild,
  onRename,
  onDelete,
  onManageDomains,
  onOpenUrl,
  onCardClick,
  boundServicesByApp = {},
  onOpenBoundService,
}) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  // Show skeletons while loading
  if (loading) {
    return (
      <Grid container spacing={2}>
        {[...Array(6)].map((_, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
            <AppCardSkeleton />
          </Grid>
        ))}
      </Grid>
    );
  }

  // Show empty state
  if (!apps || apps.length === 0) {
    return <EmptyState isDarkMode={isDarkMode} />;
  }

  return (
    <Grid
      container
      spacing={2}
      component={motion.div}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <AnimatePresence mode="popLayout">
        {apps.map((app) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            key={app.id}
            component={motion.div}
            variants={itemVariants}
            layout
            exit="exit"
          >
            <UnifiedAppCard
              app={app}
              onOpenPipeline={onOpenPipeline}
              onRestart={onRestart}
              onRebuild={onRebuild}
              onRename={onRename}
              onDelete={onDelete}
              onManageDomains={onManageDomains}
              onOpenUrl={onOpenUrl}
              onClick={onCardClick}
              boundServices={boundServicesByApp[app?.name] || []}
              onOpenBoundService={onOpenBoundService}
            />
          </Grid>
        ))}
      </AnimatePresence>
    </Grid>
  );
}

export default CardGridView;
